import type { ListRenderItemInfo } from '@shopify/flash-list';
import { FlashList } from '@shopify/flash-list';
import {
  NotificationGeneric,
  NotificationReminder,
  NotificationSkeleton,
  NotificationSuperChanged,
  NotificationWelcome,
} from 'app/components/notification';
import { TopLoadingIndicator, useTopLoadingIndicator } from 'app/components/top-loading-indicator';
import { Column } from 'app/design/primitives/box';
import { isInLast24Hours, isInLastWeek } from 'app/lib/date';
import { useAllNotifications } from 'app/network/notification';
import { useOnFocus } from 'app/services/on-focus';
import type {
  GenericNotificationType,
  NotificationType,
  ReminderNotificationType,
  SuperChangedNotificationType,
} from 'app/types/notification';
import { NotificationTemplateIdentifier } from 'app/types/notification';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { usePageTitle } from 'app/utils/web-helpers';
import { useEffect, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator } from 'react-native';
import { SectionHeader } from './section-header';
import { EmptyState } from './empty-state';
import { ItemSeparator } from './item-separator';

const supportedNotificationTemplates = new Set(Object.values(NotificationTemplateIdentifier));

const renderNotificationTemplate: Record<
  NotificationTemplateIdentifier,
  (args: ListRenderItemInfo<NotificationType>) => JSX.Element
> = {
  generic: ({ item }) => <NotificationGeneric {...(item as GenericNotificationType)} />,
  reminder: ({ item }) => <NotificationReminder {...(item as ReminderNotificationType)} />,
  welcome: ({ item }) => <NotificationWelcome {...item} />,
  // 'impact-level': ({ item }) => <NotificationImpactLevel {...item} />,
  superChanged: ({ item }) => <NotificationSuperChanged {...(item as SuperChangedNotificationType)} />,
};

type SectionKey = 'today' | 'thisWeek' | 'earlier';

function getSectionKey(createdAt: string): SectionKey {
  const date = new Date(createdAt);
  if (isInLast24Hours(date)) return 'today';
  if (isInLastWeek(date)) return 'thisWeek';
  return 'earlier';
}

export function AllNotificationsContent({ fill, screenName }: { fill?: boolean; screenName: string }) {
  const query = useAllNotifications({
    throwOnError: true,
    markAsRead: true,
  });
  const { refetch, isLoading, isPending, data, fetchNextPage, isFetchingNextPage, resetThenRefetch, hasNextPage } =
    query;

  useRegisterLoading(isPending, screenName);

  const { t } = useTranslation();

  const setPageTitle = usePageTitle('notifications');
  useEffect(() => setPageTitle(t('tabBarLabel', { ns: 'notifications' })), [setPageTitle, t]);

  useOnFocus(() => {
    void refetch();
  });

  const loadingState = useTopLoadingIndicator(query);

  const handleEndReached = () => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  const filteredData =
    data?.pages
      .map((page) => page.data)
      .flat()
      .filter((item) => supportedNotificationTemplates.has(item.templateIdentifier)) ?? [];

  const sectionIndices = (() => {
    const indices: number[] = [];
    let lastSection: SectionKey | null = null;
    filteredData.forEach((item, index) => {
      const section = getSectionKey(item.createdAt);
      if (section !== lastSection) {
        indices.push(index);
        lastSection = section;
      }
    });
    return indices;
  })();

  const renderItem = (args: ListRenderItemInfo<NotificationType>) => {
    const sectionKey = getSectionKey(args.item.createdAt);
    const showHeader = sectionIndices.includes(args.index);
    return (
      <>
        {showHeader ? <SectionHeader sectionKey={sectionKey} /> : null}
        {renderNotificationTemplate[args.item.templateIdentifier](args)}
      </>
    );
  };

  return (
    <FlashList
      ListEmptyComponent={
        isLoading ? (
          <>
            <NotificationSkeleton />
            <NotificationSkeleton />
            <NotificationSkeleton />
            <NotificationSkeleton />
            <NotificationSkeleton />
          </>
        ) : filteredData.length === 0 ? (
          <EmptyState />
        ) : null
      }
      ListFooterComponent={
        isFetchingNextPage ? (
          <Column marginTop="spacing.general.x8">
            <ActivityIndicator />
          </Column>
        ) : null
      }
      ListHeaderComponent={<TopLoadingIndicator isLoading={loadingState.isLoading} position="relative" />}
      ItemSeparatorComponent={ItemSeparator}
      data={filteredData}
      keyExtractor={(item) => item._id}
      onEndReached={handleEndReached}
      onEndReachedThreshold={2}
      onRefresh={resetThenRefetch}
      refreshing={false}
      renderItem={renderItem}
      style={fill ? { flex: 1 } : undefined}
    />
  );
}
